import { includes } from "lodash";
import moment from "moment";
import Avatar from "react-avatar";
import { useIntl } from "react-intl";
import { filterParams } from "src/app/components/grid/GridHelpers";
import { SimpleEntityGridColumn } from "src/app/components/grid/SimpleEntityGrid";
import { links } from "src/app/types/orders/order";

export default function useWaitingGridColumns() {
    const { formatMessage } = useIntl();

    const gridColumns: Array<SimpleEntityGridColumn> = [
        {
            field: "id",
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 60,
            maxWidth: 60,
            hide: true,
        },
        {
            field: "publishedAt",
            filter: "agDateColumnFilter",
            comparator: (date1, date2) => {
                const timeStamp1 = new Date(
                    moment(date1, "DD/MM/YYYY").format("YYYY-MM-DD")
                ).getTime();
                const timeStamp2 = new Date(
                    moment(date2, "DD/MM/YYYY").format("YYYY-MM-DD")
                ).getTime();
                return timeStamp1 - timeStamp2;
            },
            headerName: formatMessage({ id: "waiting.grid.col.publishedAt" }),
            valueFormatter: ({ data }: any) => moment(data.publishedAt).format("DD/MM/YYYY"),
            valueGetter: ({ data }) => `${moment(data.publishedAt).format("DD/MM/YYYY")}`,
            cellClassRules: {
                "order-fail": ({ data }: any) =>
                    moment(data.publishedAt).unix() <= moment().subtract(1, "d").unix(),
            },
            filterParams,
            minWidth: 100,
            maxWidth: 100,
        },
        {
            field: "manager",
            headerName: formatMessage({ id: "content.grid.col.manager" }),
            autoHeight: true,
            valueGetter: ({ data }) =>
                data?.manager ? `${data.manager.firstName} ${data.manager.lastName}` : "",
            cellRenderer: ({ data }: any) => (
                <>
                    {data.manager && data?.manager?.firstName && data?.manager?.lastName && (
                        <div>
                            <Avatar
                                round
                                className={
                                    includes(data.manager.role, "LEAD") ? "avatar-is-Leader" : ""
                                }
                                size="24px"
                                fgColor={
                                    includes(data.manager.role, "LEAD")
                                        ? "var(--kt-primary-inverse)"
                                        : "var(--kt-secondary-inverse)"
                                }
                                color={
                                    includes(data.manager.role, "LEAD")
                                        ? "var(--kt-primary)"
                                        : "var(--kt-secondary)"
                                }
                                textSizeRatio={2}
                                name={`${data?.manager?.firstName} ${data?.manager?.lastName} `}
                            />
                        </div>
                    )}
                </>
            ),
            filter: "agSetColumnFilter",
            minWidth: 60,
            maxWidth: 60,
        },
        {
            field: "platOrMoney",
            valueGetter: ({ data }) =>
                data?.platform ? data?.platform?.name : data?.moneySite?.domain,
            headerName: formatMessage({ id: "content.grid.col.moneySite" }),
            filter: "agMultiColumnFilter",
            minWidth: 200,
            maxWidth: 200,
        },
        {
            field: "rd.domain",
            headerName: formatMessage({ id: "content.grid.col.rd" }),
            minWidth: 200,
            maxWidth: 200,
        },
        {
            field: "sentAt",
            headerName: formatMessage({ id: "content.grid.col.sentAt" }),
            valueFormatter: ({ data }: any) => moment(data.sentAt).format("DD/MM/YYYY"),
            valueGetter: ({ data }) => `${moment(data.sentAt).format("DD/MM/YYYY")}`,
            filterParams,
            filter: "agDateColumnFilter",
            comparator: (date1, date2) => {
                const timeStamp1 = new Date(
                    moment(date1, "DD/MM/YYYY").format("YYYY-MM-DD")
                ).getTime();
                const timeStamp2 = new Date(
                    moment(date2, "DD/MM/YYYY").format("YYYY-MM-DD")
                ).getTime();
                return timeStamp1 - timeStamp2;
            },
            minWidth: 100,
            maxWidth: 100,
        },
        {
            field: "writingType",
            headerName: formatMessage({ id: "content.grid.col.contentType" }),
            valueGetter: ({ data }: any) =>
                `${formatMessage({
                    id: `content.grid.col.contentType.${data.contentType}`,
                })} ${formatMessage({
                    id: `content.grid.col.writingType.${data.content.writingType}`,
                })}`,
            minWidth: 100,
            maxWidth: 100,
        },
        {
            field: "content.title",
            headerName: formatMessage({ id: "content.grid.col.title" }),
        },
        {
            field: "keyword",
            autoHeight: true,
            headerName: formatMessage({ id: "content.grid.col.keyword" }),
            valueGetter: ({ data }) => data?.keywords,
            cellRenderer: ({ data }: any) => (
                <div className="thematic-react-tag-wrapper gap-2 pb-2 pt-2">
                    {data?.keywords?.map((category: string, index: number) => (
                        <div key={index} className="thematic-react-tag">
                            {category}
                        </div>
                    ))}
                </div>
            ),
            width: 200,
        },
        {
            field: "links",
            autoHeight: true,
            headerName: formatMessage({ id: "content.grid.col.links" }),
            valueGetter: ({ data }: any) =>
                data?.links
                    ? data.links[1]
                        ? `${data.links[0].url} (${data.links[0].anchor})\n ${data.links[1].url} (${data.links[1].anchor})`
                        : `${data.links[0].url} (${data.links[0].anchor})`
                    : "",
            cellRenderer: ({ data }: any) => (
                <ul className="padding-start-0 mb-0">
                    {data?.links?.map((link: links, index: number) => (
                        <li key={index} style={{ listStyleType: "none" }}>
                            {link.url} ({link.anchor})
                        </li>
                    ))}
                </ul>
            ),
            width: 360,
        },
        {
            field: "rd.pbn.name",
            headerName: formatMessage({ id: "content.grid.col.network" }),
            minWidth: 80,
            maxWidth: 80,
        },
        {
            field: "commentForLinker",
            headerName: formatMessage({ id: "content.grid.col.commentForLinker" }),
            filter: "agTextColumnFilter",
            hide: true,
        },
        {
            field: "createdAt",
            filter: "agDateColumnFilter",
            comparator: (date1, date2) => {
                const timeStamp1 = new Date(
                    moment(date1, "DD/MM/YYYY").format("YYYY-MM-DD")
                ).getTime();
                const timeStamp2 = new Date(
                    moment(date2, "DD/MM/YYYY").format("YYYY-MM-DD")
                ).getTime();
                return timeStamp1 - timeStamp2;
            },
            headerName: formatMessage({ id: "content.grid.col.createdAt" }),
            valueFormatter: ({ data }: any) => moment(data.createdAt).format("DD/MM/YYYY"),
            valueGetter: ({ data }: any) => moment(data.createdAt).format("DD/MM/YYYY"),
            filterParams,
            minWidth: 130,
            maxWidth: 130,
            hide: true,
        },
        {
            field: "updatedAt",
            filter: "agDateColumnFilter",
            comparator: (date1, date2) => {
                const timeStamp1 = new Date(
                    moment(date1, "DD/MM/YYYY").format("YYYY-MM-DD")
                ).getTime();
                const timeStamp2 = new Date(
                    moment(date2, "DD/MM/YYYY").format("YYYY-MM-DD")
                ).getTime();
                return timeStamp1 - timeStamp2;
            },
            headerName: formatMessage({ id: "content.grid.col.updatedAt" }),
            valueFormatter: ({ data }: any) => moment(data.updatedAt).format("DD/MM/YYYY"),
            valueGetter: ({ data }: any) => moment(data.updatedAt).format("DD/MM/YYYY"),
            filterParams,
            minWidth: 130,
            maxWidth: 130,
            hide: true,
        },
    ];
    return gridColumns;
}
